'use client'

import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building,
  Briefcase,
  Clock,
  AlertTriangle
} from 'lucide-react'

export default function ProfilePage() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<'personal' | 'professional'>('personal')

  // Loading state
  if (!user) {
    return (
      
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto"></div>
            <p className="text-brand-gray-light font-body">Carregando perfil...</p>
          </div>
        </div>
      
    )
  }

  const renderPersonalTab = () => (
    <div className="space-y-6">
      <h3 className="font-display text-lg font-semibold text-white">
        Informações Pessoais
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-brand-gray mb-2">
            Nome Completo
          </label>
          <p className="text-white">{user.employee?.personalInfo?.name}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-gray mb-2">
            Email
          </label>
          <p className="text-white">{user.email}</p>
        </div>

        {user.employee?.personalInfo?.phone && (
          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">
              Telefone
            </label>
            <p className="text-white">{user.employee.personalInfo.phone}</p>
          </div>
        )}

        {user.employee?.personalInfo?.birthDate && (
          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">
              Data de Nascimento
            </label>
            <p className="text-white">
              {new Date(user.employee.personalInfo.birthDate).toLocaleDateString('pt-BR')}
            </p>
          </div>
        )}

        {user.employee?.personalInfo?.address && typeof user.employee.personalInfo.address === 'object' && (
          <>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-brand-gray mb-2">
                Endereço
              </label>
              <p className="text-white">{user.employee.personalInfo.address.street}</p>
            </div>

            {user.employee.personalInfo.address.city && (
              <div>
                <label className="block text-sm font-medium text-brand-gray mb-2">
                  Cidade
                </label>
                <p className="text-white">{user.employee.personalInfo.address.city}</p>
              </div>
            )}

            {user.employee.personalInfo.address.state && (
              <div>
                <label className="block text-sm font-medium text-brand-gray mb-2">
                  Estado
                </label>
                <p className="text-white">{user.employee.personalInfo.address.state}</p>
              </div>
            )}

            {user.employee.personalInfo.address.zipCode && (
              <div>
                <label className="block text-sm font-medium text-brand-gray mb-2">
                  CEP
                </label>
                <p className="text-white">{user.employee.personalInfo.address.zipCode}</p>
              </div>
            )}
          </>
        )}

        {user.employee?.personalInfo?.emergencyContact && typeof user.employee.personalInfo.emergencyContact === 'object' && (
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-brand-gray mb-2">
              Contato de Emergência
            </label>
            <p className="text-white">{user.employee.personalInfo.emergencyContact.name} - {user.employee.personalInfo.emergencyContact.phone}</p>
          </div>
        )}
      </div>
    </div>
  )

  const renderProfessionalTab = () => {
    if (!user.employee) {
      return (
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            Dados profissionais não disponíveis
          </h3>
          <p className="text-gray-400">
            Você não está cadastrado como colaborador no sistema.
          </p>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        <h3 className="font-display text-lg font-semibold text-white">
          Informações Profissionais
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">
              ID do Colaborador
            </label>
            <p className="text-white">{user.employee.employeeId}</p>
          </div>

          {user.employee.employment?.department?.name && (
            <div>
              <label className="block text-sm font-medium text-brand-gray mb-2">
                Departamento
              </label>
              <p className="text-white">{user.employee.employment.department.name}</p>
            </div>
          )}

          {user.employee.employment?.role?.name && (
            <div>
              <label className="block text-sm font-medium text-brand-gray mb-2">
                Cargo
              </label>
              <p className="text-white">{user.employee.employment.role.name}</p>
            </div>
          )}

          {user.employee.employment?.contractType && (
            <div>
              <label className="block text-sm font-medium text-brand-gray mb-2">
                Tipo de Contrato
              </label>
              <p className="text-white">{user.employee.employment.contractType}</p>
            </div>
          )}

          {user.employee.employment?.status && (
            <div>
              <label className="block text-sm font-medium text-brand-gray mb-2">
                Status
              </label>
              <p className="text-white capitalize">{user.employee.employment.status.toLowerCase()}</p>
            </div>
          )}

          {user.employee.employment?.startDate && (
            <div>
              <label className="block text-sm font-medium text-brand-gray mb-2">
                Data de Contratação
              </label>
              <p className="text-white">
                {new Date(user.employee.employment.startDate).toLocaleDateString('pt-BR')}
              </p>
            </div>
          )}


          {user.employee.employment?.managerId && (
            <div>
              <label className="block text-sm font-medium text-brand-gray mb-2">
                ID do Gestor
              </label>
              <p className="text-white">{user.employee.employment.managerId}</p>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
              Meu Perfil
            </h1>
            <p className="text-brand-gray-light font-body text-lg">
              Visualize suas informações pessoais e profissionais
            </p>
          </div>
        </div>

        {/* Profile Card */}
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="relative mx-auto sm:mx-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-brand-primary rounded-full flex items-center justify-center">
                <User size={32} className="text-brand-dark" />
              </div>
            </div>

            <div className="text-center sm:text-left flex-1">
              <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-1">
                {user.employee?.personalInfo?.name || user.name}
              </h2>
              {user.employee?.employment?.role?.name && (
                <p className="text-brand-primary font-medium text-sm sm:text-base mb-2">
                  {user.employee.employment.role.name}
                </p>
              )}
              {user.employee?.employment?.department?.name && (
                <p className="text-brand-gray text-sm">
                  {user.employee.employment.department.name}
                </p>
              )}
            </div>

            <div className="flex flex-col space-y-2 text-sm text-brand-gray">
              <div className="flex items-center">
                <Mail className="mr-2" size={16} />
                {user.email}
              </div>
              {user.employee?.employeeId && (
                <div className="flex items-center">
                  <Briefcase className="mr-2" size={16} />
                  ID: {user.employee.employeeId}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-brand-dark-light/80 backdrop-blur-sm rounded-monumenta p-1 shadow-monumenta border border-gray-800/50">
          <div className="flex space-x-1">
            {[
              { id: 'personal', label: 'Pessoal', icon: User },
              { id: 'professional', label: 'Profissional', icon: Building }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-3 rounded-monumenta font-display font-semibold transition-all duration-300 flex items-center gap-2 flex-1 justify-center ${
                  activeTab === tab.id
                    ? 'bg-brand-primary text-brand-dark shadow-monumenta-brand'
                    : 'text-gray-300 hover:text-white hover:bg-brand-dark-lighter'
                }`}
              >
                <tab.icon size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="card animate-fade-in-up">
          {activeTab === 'personal' && renderPersonalTab()}
          {activeTab === 'professional' && renderProfessionalTab()}
        </div>
      </div>
    
  )
}
